import { useEffect } from 'react';
import { useDashboard } from '../contexts/DashboardContext';

/**
 * useTabs — активная вкладка раздела
 * ==================================
 *
 * Состояние живёт в DashboardContext (и через него в URL) как id вкладки, а не
 * индекс: ссылка ?section=roas&tab=roas-monthly остаётся валидной, даже если
 * вкладки переставят. Наружу отдаём индекс — так его ждёт TabsContainer.
 */

const readStoredTab = (sectionName) => {
  try {
    const raw = localStorage.getItem(`${sectionName}-active-tab`);
    return raw === null ? null : JSON.parse(raw);
  } catch (error) {
    console.error(`Error loading active tab for "${sectionName}":`, error);
    return null;
  }
};

// Поддерживаем оба формата: id (текущий) и числовой индекс (как хранилось раньше)
// legacyTabIds — порядок вкладок, к которому относились числовые индексы, если
// набор вкладок с тех пор менялся (иначе индекс указал бы на чужую вкладку).
// Устаревший id/индекс без соседа среди текущих вкладок даёт null → первая вкладка.
const resolveTabId = (stored, tabIds, legacyTabIds) => {
  if (typeof stored === 'string' && tabIds.includes(stored)) return stored;
  if (typeof stored === 'number') {
    const candidate = (legacyTabIds || tabIds)[stored];
    if (candidate && tabIds.includes(candidate)) return candidate;
  }
  return null;
};

export const useTabs = (sectionName, tabs = [], legacyTabIds = null) => {
  const { activeTabs, setActiveTabForSection } = useDashboard();

  const tabIds = tabs.map(tab => tab.id);
  const activeTabId = resolveTabId(activeTabs[sectionName], tabIds, legacyTabIds)
    || resolveTabId(readStoredTab(sectionName), tabIds, legacyTabIds)
    || tabIds[0];

  // Подтягиваем вкладку из localStorage в контекст, чтобы она попала в ссылку
  useEffect(() => {
    if (activeTabId && activeTabs[sectionName] !== activeTabId) {
      setActiveTabForSection(sectionName, activeTabId);
    }
  }, [sectionName, activeTabId, activeTabs, setActiveTabForSection]);

  const activeIndex = tabIds.indexOf(activeTabId);

  return {
    activeTab: activeIndex === -1 ? 0 : activeIndex,
    setActiveTab: (tabIndex) => setActiveTabForSection(sectionName, tabIds[tabIndex] || tabIds[0])
  };
};
